import { useState, useCallback } from 'react';
import { generateProgram, getProgramsByClient, getProgramById } from '../api/client';
import type { Program } from '../types';

interface UseProgramsReturn {
  programs: Program[];
  currentProgram: Program | null;
  isGenerating: boolean;
  isLoading: boolean;
  error: string | null;
  generate: (clientId: string) => Promise<Program>;
  fetchByClient: (clientId: string) => Promise<void>;
  fetchById: (id: string) => Promise<void>;
  clearError: () => void;
}

export function usePrograms(): UseProgramsReturn {
  const [programs, setPrograms] = useState<Program[]>([]);
  const [currentProgram, setCurrentProgram] = useState<Program | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generate = useCallback(async (clientId: string): Promise<Program> => {
    setIsGenerating(true);
    setError(null);
    try {
      const program = await generateProgram(clientId);
      setPrograms(prev => [program, ...prev]);
      setCurrentProgram(program);
      return program;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate program';
      setError(message);
      throw err;
    } finally {
      setIsGenerating(false);
    }
  }, []);

  const fetchByClient = useCallback(async (clientId: string): Promise<void> => {
    const token = localStorage.getItem('ptai_token');
    if (!token) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await getProgramsByClient(clientId);
      setPrograms(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch programs';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [])

  const fetchById = useCallback(async (id: string): Promise<void> => {
    const token = localStorage.getItem('ptai_token');
    if (!token) return;
    setIsLoading(true);
    setError(null);
    try {
      const program = await getProgramById(id);
      setCurrentProgram(program);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch program';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  return {
    programs,
    currentProgram,
    isGenerating,
    isLoading,
    error,
    generate,
    fetchByClient,
    fetchById,
    clearError,
  };
}
